import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Lock, Eye, EyeOff, AlertCircle, CheckCircle2, ShieldCheck } from "lucide-react";
import { supabase } from "../../lib/supabase";
import PasswordStrengthMeter from "./components/PasswordStrengthMeter";
import PasswordRequirements from "./components/PasswordRequirements";

const NewPasswordCreationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location?.state?.email || "";
  const verified = location?.state?.verified || false;

  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Redirect if not coming from verified OTP
  useEffect(() => {
    if (!email || !verified) {
      navigate("/password-reset-email-screen", { replace: true });
    }
  }, [email, verified, navigate]);

  const getPasswordStrength = (pwd) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (pwd?.length >= 12) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    return score;
  };

  const validate = () => {
    const errs = {};
    if (!formData?.password) {
      errs.password = "Password is required.";
    } else if (formData?.password?.length < 8) {
      errs.password = "Password must be at least 8 characters.";
    }
    if (!formData?.confirmPassword) {
      errs.confirmPassword = "Please confirm your password.";
    } else if (formData?.password !== formData?.confirmPassword) {
      errs.confirmPassword = "Passwords do not match.";
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (apiError) setApiError("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsLoading(true);
    setApiError("");
    try {
      // Sign in the user with OTP (email link) to get a session, then update password
      // Since we verified via custom OTP, we use admin update via service or updateUser
      // We'll use Supabase's password recovery flow: signInWithOtp then updateUser
      const { error } = await supabase?.auth?.updateUser({
        password: formData?.password,
      });
      if (error) {
        // If no active session, try to sign in first then update
        if (error?.message?.toLowerCase()?.includes("session") || error?.message?.toLowerCase()?.includes("not authenticated")) {
          setApiError("Your session has expired. Please restart the password reset process.");
        } else {
          setApiError(error?.message || "Failed to update password. Please try again.");
        }
      } else {
        setIsSuccess(true);
        setTimeout(() => {
          navigate("/login-screen", { replace: true });
        }, 3000);
      }
    } catch (err) {
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData?.password);

  if (!email || !verified) return null;

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="w-full px-6 py-4 flex items-center border-b border-border bg-white">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-white font-bold text-sm">LPS</span>
            </div>
            <span className="font-semibold text-foreground text-sm hidden sm:block">Volunteer Portal</span>
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-2xl shadow-sm border border-border p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                Password updated!
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Your password has been successfully updated. You'll be redirected to the login page in a moment.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <svg className="animate-spin w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Redirecting to login…
              </div>
              <Link
                to="/login-screen"
                className="mt-4 inline-block text-sm text-primary font-medium hover:text-primary/80 transition-colors"
              >
                Go to Login now
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="w-full px-6 py-4 flex items-center justify-between border-b border-border bg-white">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-white font-bold text-sm">LPS</span>
          </div>
          <span className="font-semibold text-foreground text-sm hidden sm:block">Volunteer Portal</span>
        </Link>
        <button
          onClick={() => navigate("/password-reset-otp-verification-screen", { state: { email } })}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-border p-8">
            {/* Icon + Heading */}
            <div className="flex flex-col items-center mb-8">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <ShieldCheck className="w-7 h-7 text-primary" />
              </div>
              <h1
                className="text-2xl font-bold text-foreground mb-1.5 text-center"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Create new password
              </h1>
              <p className="text-sm text-muted-foreground text-center">
                Choose a strong password to secure your account.
              </p>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs font-medium text-green-600">Email</span>
              </div>
              <div className="flex-1 h-px bg-green-500" />
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs font-medium text-green-600">Verified</span>
              </div>
              <div className="flex-1 h-px bg-primary" />
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">3</div>
                <span className="text-xs font-medium text-primary">New Password</span>
              </div>
            </div>

            {/* API Error */}
            {apiError && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 mb-5">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700">{apiError}</p>
                  {apiError?.includes("expired") && (
                    <button
                      onClick={() => navigate("/password-reset-email-screen")}
                      className="mt-1.5 text-xs text-red-600 underline hover:text-red-800"
                    >
                      Restart password reset
                    </button>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              {/* New Password */}
              <div className="space-y-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={formData?.password}
                    onChange={handleChange}
                    placeholder="Min. 8 characters"
                    className={`w-full pl-10 pr-11 py-3 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                      errors?.password
                        ? "border-red-400 focus:ring-red-300 bg-red-50" :"border-gray-200 focus:ring-primary/30 focus:border-primary bg-white"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors?.password && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {errors?.password}
                  </p>
                )}
                {/* Strength Meter */}
                {formData?.password && (
                  <PasswordStrengthMeter strength={passwordStrength} />
                )}
              </div>

              {/* Password Requirements */}
              {formData?.password && (
                <PasswordRequirements password={formData?.password} />
              )}

              {/* Confirm Password */}
              <div className="space-y-1.5">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirm ? "text" : "password"}
                    autoComplete="new-password"
                    value={formData?.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter your password"
                    className={`w-full pl-10 pr-11 py-3 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                      errors?.confirmPassword
                        ? "border-red-400 focus:ring-red-300 bg-red-50"
                        : formData?.confirmPassword && formData?.password === formData?.confirmPassword
                        ? "border-green-400 focus:ring-green-300 bg-green-50" :"border-gray-200 focus:ring-primary/30 focus:border-primary bg-white"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                  >
                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors?.confirmPassword && (
                  <p className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {errors?.confirmPassword}
                  </p>
                )}
                {formData?.confirmPassword && formData?.password === formData?.confirmPassword && !errors?.confirmPassword && (
                  <p className="flex items-center gap-1.5 text-xs text-green-600">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                    Passwords match
                  </p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg bg-primary text-white font-semibold text-sm transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Updating password…
                  </>
                ) : (
                  "Update Password"
                )}
              </button>
            </form>

            {/* Security note */}
            <div className="mt-6 p-3 bg-muted/40 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground text-center">
                🔒 Use a strong, unique password you don't use on other sites.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewPasswordCreationScreen;
